/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
// import { ICallToActionProps } from '@msdyn365-commerce-modules/shared-components/dist/types/call-to-action/CallToAction.props';
import { Button, Heading } from '@msdyn365-commerce-modules/utilities';
import { IImageData, IImageSettings, Image} from '@msdyn365-commerce/core';
import {observer} from 'mobx-react';
import * as React from 'react';
import {createAccordionStateInput} from '../../data-actions/accordion/accordion-state-data-action';
import { ICittaAccordionCustomData } from './citta-accordion-custom.data';
import { ICittaAccordionCustomProps, toggleAllFormat } from './citta-accordion-custom.props.autogenerated';
export interface ICallToActionProps {
  title: string;
  color: string;
  glyph?:string;
  layout: string;
  'aria-label'?: string;
}
/**
 *
 * Accordion component
 * @extends {React.Component<IAccordionProps<ICittaAccordionCustomData>>}
 */
@observer
class CittaAccordionCustom extends React.Component<ICittaAccordionCustomProps<ICittaAccordionCustomData>> {
  // @ts-ignore
  private itemRefs: React.RefObject[] = [];
  // @ts-ignore
  private accordionHeaderAnchorRef: React.RefObject;
  // @ts-ignore
  private accordionItemsAnchorRef: React.RefObject;

  constructor(props: ICittaAccordionCustomProps<ICittaAccordionCustomData>) {
    super(props);
    this._onExpandAll = this._onExpandAll.bind(this);
    this._onCollapseAll = this._onCollapseAll.bind(this);
    const accordionState = {expandSingleItemOnly: this.props.config.expandSingleItemOnly, radioButtonText: ''};
    this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
  }

  public componentDidMount(): void {
    const isDesignTeamAccordion = this.props.config.renderAsDesignTeam;
    if (isDesignTeamAccordion) {
      if (!this.accordionItemsAnchorRef) {
        this.accordionItemsAnchorRef = React.createRef();
      }
      if (!this.accordionHeaderAnchorRef) {
        this.accordionHeaderAnchorRef = React.createRef();
      }
    }
  }

  public render(): JSX.Element {
    const config = this.props && this.props.config;
    const resources= this.props && this.props.resources;
    const headingText = this.props && this.props.config && this.props.config.accordionTitle;
    const callToActionProps = this._getCallToActionProps(config && config.toggleAllFormat === toggleAllFormat.text);

    const isDesignTeamAccordion = this.props.config.renderAsDesignTeam;

    const hideDesignTeamTitles = this.props.config.hideDesignTeamTitles || false;

    const wrapperClass = `accordion citta-accordion-custom ${isDesignTeamAccordion && `citta-accordion-design-team ${hideDesignTeamTitles && 'citta-accordion-design-team-hide-tiles'}`}`;

    return (
      <div className = {wrapperClass}>
        <span className='citta-accordion-custom-anchor' ref={this.accordionHeaderAnchorRef}/>
        <div className = 'accordion-header'>
          { headingText && (
            <div className = 'accordion-header-text'>
              <Heading headingTag='h2' text={headingText} />
            </div>)
          }
          { (config && config.showToggleAll) && (
            <div className = 'accordion-header-toggle-all'>
              <button onClick = {this._onExpandAll} {...callToActionProps[0]} />
              &nbsp;{resources.toggleAllSeparator}&nbsp;
              <button onClick = {this._onCollapseAll} {...callToActionProps[1]} />
            </div>)
          }
        </div>
        { isDesignTeamAccordion && (this._renderDesignerTiles()) }
        <div className='accordion-item-section'>
          <span className='citta-accordion-custom-anchor' ref={this.accordionItemsAnchorRef}/>
          {this._renderAccordionItems()}
          { isDesignTeamAccordion && config && config.showBackToTopButton && (
            <Button
              onClick={this._backToTop()}
              className='citta-accordion-design-team-backtotop'
            >
              Back to top ^
            </Button>
          ) }
        </div>
      </div>
    );
  }

  private _renderAccordionItems(): JSX.Element[] | null {
    const accordionItems = this.props.slots && this.props.slots.accordionItem && this.props.slots.accordionItem.length ? this.props.slots.accordionItem : [];
    if (this.itemRefs.length !== accordionItems.length) {
      this.itemRefs = Array(accordionItems.length).fill(0).map((_, i) => React.createRef());
    }

    return (accordionItems.map((slot, index) => {
      return (
        <div key={index} ref={this.itemRefs[index]}>
          {slot}
        </div>
      );
    }));
  }
  private _renderDesignerImage(index: number, imageSettings?: IImageSettings, imageUrl?: string,  altText: string = ''): JSX.Element | null {
    const gridSettings = this.props.context.request.gridSettings!;
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText
    };
    const imageProps = {
        gridSettings: gridSettings,
        imageSettings: imageSettings
    };
    return (
        <Image {...img} {...imageProps} loadFailureBehavior='empty' onClick = {this._toggleSection(index)}/>
    );
}

  private _renderDesignerTiles(): JSX.Element | null {
    const designTeamDetails = this.props.config.designTeamDetails;

    if (!designTeamDetails) {
      return null;
    }

    const defaultImageSettings: IImageSettings = {
      viewports: {
          xs: { q: `w=480&h=680&m=6`, w: 0, h: 0 },
          lg: { q: `w=480&h=680&m=6`, w: 0, h: 0 }
      },
      lazyload: true
    };

    return (
      <div className='citta-accordion-design-team-tiles container'>
        {designTeamDetails.map((tile, index) => {
          return (
            <div key={index} className='citta-accordion-design-team-tile'>
              <div className='citta-accordion-design-team-tile-content'>
                <h6>{tile.jobTitle}</h6>
                <h3 onClick={this._toggleSection(index)}>{tile.name}</h3>
                <p>"{tile.quote}"</p>
                <Button onClick={this._toggleSection(index)}>
                  Read More <i className='call-to-action__glyph fas fa-chevron-down' aria-hidden='true'/>
                </Button>
              </div>
              <div className='citta-accordion-design-team-tile-image' >
                {tile.image && this._renderDesignerImage(index, tile.image.imageSettings || defaultImageSettings,
                                                         tile.image?.src, tile.image?.altText)
                }
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  private _toggleSection = (index: number) => (): void => {
    if (index >= 0 && index < this.itemRefs.length) {
      const accordionItem = this.itemRefs[index].current;
      if (accordionItem) {
        const buttonElem = accordionItem.getElementsByClassName('accordion-item-custom-button')[0];
        if (buttonElem) {
          buttonElem.click();
          if (this.accordionItemsAnchorRef && this.accordionItemsAnchorRef.current) {
            const elem = (this.accordionItemsAnchorRef.current.getBoundingClientRect().top as number) + window.scrollY;
            window.scroll({
              top: elem,
              behavior: 'smooth'
            });
          }
        }
      }
    }
  }

  private _backToTop = () => (): void => {
    if (this.accordionHeaderAnchorRef && this.accordionHeaderAnchorRef.current) {
      const elem = (this.accordionHeaderAnchorRef.current.getBoundingClientRect().top as number) + window.scrollY;
      window.scroll({
        top: elem,
        behavior: 'smooth'
      });
    }
  }

  private _getCallToActionProps(isToggleAllTextFormat: boolean): ICallToActionProps[] {
    const resources =this.props && this.props.resources;
    if(isToggleAllTextFormat) {
      return [{
        title: resources.expandAllText || '',
        'aria-label': this.props && this.props['aria-label'],
        color:'link',
        layout:'noGlyph'
      },
      {
        title: resources.collapseAllText || '',
        'aria-label': this.props && this.props['aria-label'],
        color:'link',
        layout:'noGlyph'
      }
    ];
    }
    return [
      {
        title: '',
        'aria-label': this.props && this.props['aria-label'],
        color:'link',
        layout:'glyphOnly',
        glyph: 'expand-all-glyph'
      },
      {
        title: '',
        'aria-label': this.props && this.props['aria-label'],
        color:'link',
        layout:'glyphOnly',
        glyph: 'collapse-all-glyph'
      }
      ];
    }

  private _onExpandAll(): void {
    const accordionState = {isAllExpanded: true, expandSingleItemOnly: this.props.config.expandSingleItemOnly};
    this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
  }

  private _onCollapseAll(): void {
    const accordionState = {isAllExpanded: false, expandSingleItemOnly: this.props.config.expandSingleItemOnly};
    this.props.context.actionContext.update(createAccordionStateInput(accordionState), accordionState);
  }
}

export default CittaAccordionCustom;
